import { useState, useEffect, useRef } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { IPaymentData } from "../../typings/interfaces/Payment/PaymentData";
import Loader from "../../components/molecules/Loader";
import { getPaymentIntentByID, submitPayment } from "../../api/apimethods";

interface PayNowPopupProps {
  paymentIntentData: IPaymentData;
}

export default function PayNowPopup({ paymentIntentData }: PayNowPopupProps) {
  const [show, setShow] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Refs for managing lifecycle and polling
  const pollingInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMounted = useRef(false);

  // set isMounted on mount/unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      stopPolling();
    };
  }, []);

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  const handleClose = () => {
    setShow(false);
    stopPolling();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(""); // Clear previous messages
    setIsLoading(true);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: paymentIntentData?.name || "",
            email: paymentIntentData?.email || "",
          },
        },
      },
      redirect: "if_required",
    });

    if (error) {
      if (isMounted.current) {
        setMessage(error.message || "Unexpected error.");
        setIsLoading(false);
      }
      return;
    }

    if (!paymentIntent) {
      if (isMounted.current) {
        setMessage("Waiting for authorization...");
      }
      return;
    }

    // Start polling the backend
    startPaymentPolling(paymentIntent.id);
  };

  const startPaymentPolling = (paymentIntentId: string) => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 10; // 10 * 3s = 30 seconds timeout

    pollingInterval.current = setInterval(async () => {
      if (!isMounted.current || attempts >= maxAttempts) {
        stopPolling();
        if (isMounted.current) {
          setIsLoading(false);
          setMessage("Payment confirmation timed out. Please check your email or contact support.");
        }
        return;
      }

      attempts++;

      try {
        const payment = await getPaymentIntentByID(paymentIntentId);
        const status = payment?.data?.paymentStatus;

        if (status === "succeeded") {
          stopPolling();

          if (isMounted.current) {
            setMessage(`Payment succeeded: ${payment?.data?.id}`);

            // Submit final payment record to backend
            await submitPayment(paymentIntentData);

            // Construct Success URL
            const queryParams = new URLSearchParams({
              transaction: paymentIntentData?.paymentIntentId || "",
              reference: paymentIntentData?.referenceNumber || "",
              name: paymentIntentData?.name || "",
              email: paymentIntentData?.email || "",
              amount: paymentIntentData?.amount?.toString() || "",
              dtpayment: String(payment?.data?.createdDate || new Date().toISOString())
            });

            window.location.href = `/eservices/success?${queryParams.toString()}`;
          }
        }
        else if (["failed", "canceled", "requires_payment_method"].includes(status)) {
          stopPolling();
          if (isMounted.current) {
            setIsLoading(false);
            setMessage("Payment failed or canceled.");
            window.location.href = `/eservices/payment-failed`;
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000); // Poll every 3 seconds
  };

  return (
    <>
      <button className="btn btn-success" onClick={() => setShow(true)}>
        PayNow QR Payment
      </button>

      {show && (
        <div className="modal fade show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">PayNow QR Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>

                  <div className="mb-3">
                    <PaymentElement
                      options={{
                        layout: "accordion",
                      }}
                    />
                  </div>

                  <button
                    disabled={isLoading || !stripe || !elements}
                    id="submit"
                    type="submit"
                    className="btn btn-primary w-100 mt-3"
                  >
                    <span id="button-text">
                      {isLoading ? <div className="spinner" id="spinner"></div> : "Pay Now"}
                    </span>
                  </button>

                  {message && <div id="payment-message" className="mt-2 text-center alert alert-info">{message}</div>}
                  {isLoading && <Loader />}
                </form>
              </div>

            </div>
          </div>
        </div>
      )}

      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
